import React, { ComponentProps, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Anchor,
  Box,
  HintPlacement,
  Inscription,
  SimpleHint,
  Text,
  useIsCardMailWithin7Days,
  useResponsive,
} from '@common-fe/common-fe';
import { toNumber } from 'lodash';

import AppButton from '@/components/controls/AppButton';
import {
  ATTEMPTS_LIMIT,
  ATTEMPTS_LIMIT_KEY,
  ATTEMPTS_LIMIT_TIMER_KEY,
  VERIFICATION_LIMIT_KEY,
} from '@/modules/core/components/Dashboard/components/BannersList/CompleteBanner/components/VerifyMyCardModal/VerifyMyCardModal.constants';
import { useGetCardsQuery } from '@/modules/transaction/queries/useGetCards.query';
import { useAuthStore } from '@/modules/user/stores';
import { useTimer } from '@/utils/hooks';

import useCardHolderQuery from './useCardHolder.query';

const BUTTON_WIDTH = '180px';

type ButtonType = ComponentProps<typeof AppButton>;

interface Props extends Omit<ButtonType, 'children'> {
  handleOpenModal?: () => void;
  isWarningMode?: boolean;
  isBannerMode?: boolean;
  isLinkMode?: boolean;
  children?: React.ReactNode;
}

const VerifyEmployeeButton: React.FC<Props> = ({
  handleOpenModal,
  isWarningMode,
  isLinkMode,
  isBannerMode,
  children,
  ...rest
}) => {
  const { isMobile } = useResponsive();
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const attemptLimitFromStorage = toNumber(localStorage.getItem(ATTEMPTS_LIMIT_KEY)) || 0;
  const [attempt, setAttempt] = useState(attemptLimitFromStorage);
  const { isTimeElapsed } = useTimer(VERIFICATION_LIMIT_KEY);
  const { isTimeElapsed: isAttemptLimitElapsed } = useTimer(ATTEMPTS_LIMIT_TIMER_KEY);
  const { mailedDataOfLastCard, isPendingCardExists } = useCardHolderQuery();
  const { employeePendingOpenCard } = useGetCardsQuery(user?.employee?.id);
  const isMailWithin7Days = useIsCardMailWithin7Days(employeePendingOpenCard);

  const hasVerificationLimit = useMemo(
    () => !isTimeElapsed || attempt >= ATTEMPTS_LIMIT,
    [attempt, isTimeElapsed]
  );
  const isCardMailed = useMemo(() => mailedDataOfLastCard, [mailedDataOfLastCard]);
  useEffect(() => {
    if (isAttemptLimitElapsed) {
      setAttempt(0);
      localStorage.removeItem(ATTEMPTS_LIMIT_KEY);
    }
  }, [isAttemptLimitElapsed]);

  const isCardRequestStatus = useMemo(() => {
    return isPendingCardExists && !isCardMailed;
  }, [isCardMailed, isPendingCardExists]);

  const isCardOnWayStatus = useMemo(() => {
    return isPendingCardExists && !!isCardMailed && isMailWithin7Days;
  }, [isCardMailed, isMailWithin7Days, isPendingCardExists]);

  const isCardDeliveredStatus = useMemo(() => {
    return isPendingCardExists && !!isCardMailed && !isMailWithin7Days;
  }, [isCardMailed, isMailWithin7Days, isPendingCardExists]);

  const bannerVerifyHint = useMemo(() => {
    if (isCardRequestStatus || (!isCardDeliveredStatus && !isCardOnWayStatus)) {
      return (
        <Text textAlign="center" size="small" weight={500}>
          {t(
            'The card verification button will become enabled as soon as your card is created and sent to you.'
          )}
        </Text>
      );
    }
    if (hasVerificationLimit) {
      return (
        <Text textAlign="center" size="small" weight={500}>
          {t(
            'There have been too many unsuccessful attempts to verify your card. Please wait for 5 minutes before trying again.'
          )}
        </Text>
      );
    }

    return null;
  }, [hasVerificationLimit, isCardDeliveredStatus, isCardOnWayStatus, isCardRequestStatus, t]);

  if (isLinkMode) {
    if (!hasVerificationLimit && mailedDataOfLastCard) {
      return (
        <Anchor
          target="_blank"
          data-testId="verify-my-card_link"
          onClick={rest.onClick}
          disabled={hasVerificationLimit || !mailedDataOfLastCard}
        >
          {t('Learn more')}
        </Anchor>
      );
    }
    return (
      <SimpleHint
        preferAlign={isBannerMode ? HintPlacement.RIGHT : undefined}
        content={
          <Box direction="column">
            <Text size="small" textAlign="center" weight={700}>
              {t('Why is this required? ')}
            </Text>
            <Inscription textAlign="center" size="small" weight={500}>
              {t(
                'To keep your account safe, we require a quick verification step before allowing full access. This added level of security helps protect your funds and personal information. It’s fast, easy, and only takes a minute.'
              )}
            </Inscription>
          </Box>
        }
      >
        <Anchor target="_blank" data-testId="verify-my-card_link" onClick={rest.onClick} disabled>
          {t('Learn more')}
        </Anchor>
      </SimpleHint>
    );
  }

  if (isWarningMode) {
    if (!hasVerificationLimit && mailedDataOfLastCard) {
      return (
        <AppButton
          fill={isMobile}
          testId="verify-my-card"
          width={BUTTON_WIDTH}
          onClick={handleOpenModal}
          disabled={hasVerificationLimit || !mailedDataOfLastCard}
          {...rest}
        >
          {children || t('Verify Account')}
        </AppButton>
      );
    }
    return (
      <SimpleHint
        preferAlign={isBannerMode ? HintPlacement.RIGHT : undefined}
        content={
          <Box direction="column">
            <Text size="small" textAlign="center" weight={700}>
              {t('Why is this required? ')}
            </Text>
            <Inscription textAlign="center" size="small" weight={500}>
              {t(
                'To keep your account safe, we require a quick verification step before allowing full access. This added level of security helps protect your funds and personal information. It’s fast, easy, and only takes a minute.'
              )}
            </Inscription>
          </Box>
        }
      >
        <AppButton fill={isMobile} testId="verify-my-card" width={BUTTON_WIDTH} disabled {...rest}>
          {children || t('Verify Account')}
        </AppButton>
      </SimpleHint>
    );
  }

  if ((isCardMailed || user?.employee?.employeeProvidedEmail) && !hasVerificationLimit) {
    return (
      <AppButton
        fill={isMobile}
        testId="verify-my-card"
        width={BUTTON_WIDTH}
        onClick={handleOpenModal}
        {...rest}
      >
        {children || t('Verify Account')}
      </AppButton>
    );
  }

  return (
    <SimpleHint preferAlign={isBannerMode ? HintPlacement.RIGHT : undefined} content={bannerVerifyHint}>
      <AppButton
        fill={isMobile}
        testId="verify-my-card"
        width={BUTTON_WIDTH}
        onClick={handleOpenModal}
        disabled={hasVerificationLimit || (!isCardMailed && !user?.employee?.employeeProvidedEmail)}
        {...rest}
      >
        {children || t('Verify Account')}
      </AppButton>
    </SimpleHint>
  );
};

export default VerifyEmployeeButton;
